import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, Database, FileText } from "lucide-react";
import { Btn, CoachingTreeAdminSection, DataVaultSection } from "@/pages/MarketingDashboard";

export default function AdminSources() {
  const [busy, setBusy] = useState(null);
  const [csvText, setCsvText] = useState("");
  const fileRef = useRef(null);

  const run = async (label, url, opts = {}) => {
    setBusy(label);
    try {
      const { data } = await api.post(url, opts.body || undefined);
      toast.success(`${label}: ${JSON.stringify(data).slice(0, 120)}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally { setBusy(null); }
  };

  const onCsvFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      const cells = line.match(/("([^"]|"")*"|[^,]+)(,|$)/g) || [];
      const vals = cells.map((c) => c.replace(/,$/, "").replace(/^"|"$/g, "").replace(/""/g, '"').trim());
      const row = {};
      header.forEach((k, i) => (row[k] = vals[i] || ""));
      return row;
    });
  };

  const runBulkImport = async () => {
    const rows = parseCsv(csvText);
    if (!rows.length) { toast.error("CSV must have header + rows"); return; }
    setBusy("bulk");
    try {
      const { data } = await api.post("/admin/bulk-import-coaches", { rows, auto_create_invites: true });
      toast.success(`${data.coaches_added} new · ${data.invites_created} invites created · ${data.skipped_existing} skipped`);
      setCsvText("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="text-white">
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl" data-testid="admin-sources-page">
        <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><Database size={14} /> ADMIN HQ · SOURCES &amp; DATA</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">SOURCES &amp; DATA</h1>
        <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
          Feed the TKG: run scrapers, bulk-import coaches, rebuild coaching trees, and back up the database.
        </p>

        {/* HeadhunterAgent shortcuts */}
        <div className="mt-8 border border-neutral-800 bg-appcard p-5">
          <div className="text-xs tracking-widest text-volt">HEADHUNTER · SOURCE FEEDS</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Btn data-testid="run-usal" onClick={() => run("USA Lacrosse", "/admin/rescrape-usalacrosse")} busy={busy === "USA Lacrosse"}>
              Re-scrape USA Lacrosse
            </Btn>
            <Btn data-testid="run-usila" onClick={() => run("USILA", "/admin/rescrape-usila")} busy={busy === "USILA"}>
              Re-scrape USILA (members + leads)
            </Btn>
            <Btn data-testid="run-mcla" onClick={() => run("MCLA", "/admin/rescrape-mcla?year=2026&max_teams=12")} busy={busy === "MCLA"}>
              Re-scrape MCLA (12 teams)
            </Btn>
            <Btn data-testid="run-coachtracker" onClick={() => run("CoachTracker", "/admin/rescrape-coachtracker")} busy={busy === "CoachTracker"}>
              Re-scrape CoachTracker (~112 open college roles)
            </Btn>
            <Btn data-testid="run-hs-state-assocs" onClick={() => run("HS State Assocs", "/admin/rescrape-hs-state-assocs")} busy={busy === "HS State Assocs"}>
              Re-scrape HS State Assocs (~14 sources · AI-powered · 6-8 min)
            </Btn>
            <Btn data-testid="run-varsityclub" onClick={() => run("VarsityClub", "/admin/rescrape-varsityclub")} busy={busy === "VarsityClub"}>
              Re-scrape Varsity Club Lax (MCLA/club openings + auto-pitch)
            </Btn>
            <Btn data-testid="run-teamworkonline" onClick={() => run("TeamworkOnline", "/admin/rescrape-teamworkonline")} busy={busy === "TeamworkOnline"}>
              Re-scrape TeamworkOnline (IMG Academy + club lax jobs)
            </Btn>
            <Btn data-testid="run-ncaamarket" onClick={() => run("NCAA Market", "/admin/rescrape-ncaamarket")} busy={busy === "NCAA Market"}>
              Re-scrape NCAA Market (college lax coaching jobs · RSS)
            </Btn>
            <Btn data-testid="run-staff-directories" onClick={() => run("Staff Directories", "/admin/rescrape-staff-directories?limit=40")} busy={busy === "Staff Directories"}>
              Scrape College Staff Directories (Sidearm + PrestoSports · coach emails → hunter supply)
            </Btn>
          </div>
          <p className="text-[11px] text-neutral-500 mt-2">CoachTracker takes ~3 min (Playwright). HS State Assocs takes ~6-8 min (Playwright + Claude extraction; now includes SchoolJobs.com + GovernmentJobs.com). All run automatically — CoachTracker nightly 5 AM ET, State Assocs Sundays 6 AM ET, Varsity Club Wednesdays 8 AM ET, TeamworkOnline Thursdays 8 AM ET, NCAA Market daily 4:15 AM ET, Staff Directories nightly 8 AM ET (40 schools per manual run, 60 nightly; fills missing coach emails + adds new coaches, then refills the claim queue — and auto-fires a 40-school batch whenever the refill finds the queue empty).</p>
        </div>

        {/* News ingest */}
        <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="news-ingest-card">
          <div className="text-xs tracking-widest text-volt">NEWS INGEST · PASTE AN ARTICLE</div>
          <p className="text-xs text-neutral-500 mt-1">Paste an article, Facebook post, or press release — Claude extracts coaching moves and opening signals for you to approve into the TKG.</p>
          <a
            href="/ad/marketing/news-ingest"
            data-testid="news-ingest-link"
            className="mt-3 inline-flex items-center gap-2 text-xs tracking-widest text-volt heading border border-volt px-3 py-2 hover:bg-volt hover:text-white transition-colors"
          >
            <FileText size={13} /> OPEN NEWS INGEST →
          </a>
        </div>

        {/* Bulk import */}
        <div className="mt-6 border border-neutral-800 bg-appcard p-5">
          <div className="text-xs tracking-widest text-volt">BULK IMPORT (CSV)</div>
          <p className="text-xs text-neutral-500 mt-1">
            Header row required. Columns: <code>name,school,level,division,gender,role,email,source</code>.
            Email is optional; if present the coach can be directly emailed by your outreach team.
            Each row becomes an unclaimed profile + a personal claim invite.
          </p>
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            <label className="inline-flex items-center gap-2 min-h-[44px] px-4 border border-volt text-volt heading text-sm tracking-widest cursor-pointer hover:bg-volt hover:text-white transition-colors">
              <Upload size={14} /> CHOOSE CSV
              <input ref={fileRef} data-testid="bulk-csv-input" type="file" accept=".csv,text/csv" onChange={onCsvFile} className="hidden" />
            </label>
            <button
              data-testid="bulk-template-btn"
              onClick={() => setCsvText("name,school,level,division,gender,role,email,source\nJohn Smith,Greenwich HS,HS,HS Varsity,Men,Head Coach,jsmith@example.com,twitter_blitz\nMary Jones,Notre Dame Club Lacrosse,College,MCLA D1,Women,Head Coach,,twitter_blitz")}
              className="min-h-[44px] px-4 border border-neutral-700 text-xs tracking-widest hover:border-volt hover:text-volt flex items-center gap-2"
            >
              <Download size={14} /> LOAD TEMPLATE
            </button>
          </div>
          <textarea
            data-testid="bulk-csv-textarea"
            className="mt-3 w-full min-h-[160px] bg-[#070707] border border-neutral-800 px-3 py-3 text-xs text-neutral-300 font-mono outline-none focus:border-blaze"
            placeholder="Paste CSV here (or upload above)…"
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
          <button
            data-testid="run-bulk-btn"
            onClick={runBulkImport}
            disabled={busy === "bulk" || !csvText.trim()}
            className="mt-3 min-h-[48px] px-6 bg-blaze text-white heading text-base hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-2"
          >
            <Upload size={14} /> {busy === "bulk" ? "IMPORTING…" : "IMPORT + CREATE INVITES"}
          </button>
        </div>

        <CoachingTreeAdminSection />

        <DataVaultSection />
      </div>
    </div>
  );
}
